import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, TrendingUp, Trophy, Activity, Loader2, BarChart3 } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const _pct = (v) => `${Number(v || 0).toFixed(1)} %`;
const _num = (v) => Number(v || 0).toLocaleString("de-DE");

export default function AdminBriefingAbStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/briefing-ab-stats`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte A/B-Statistik nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const a = data?.variants?.a || {};
  const b = data?.variants?.b || {};
  const sigSoft = data?.significance_soft || {};
  const sigHard = data?.significance_hard || {};

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-briefing-ab-stats-page">
      <AppHeader title="Briefing A/B-Test" back="/dashboard" />
      <main className="max-w-5xl mx-auto px-3 sm:px-5 py-5 space-y-5">
        <div className="flex items-center gap-3">
          <FlaskConical className="text-violet-400" size={22} />
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-bold">PV-Briefing A/B-Test</h1>
            <p className="text-[12px] text-zinc-300 font-medium">Variante A „Zahlen-fokussiert" vs. B „Story-fokussiert" — Sticky 50/50 pro Projekt</p>
          </div>
        </div>

        {loading && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-400 inline-flex w-full justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> lädt…
          </div>
        )}

        {!loading && data && (
          <>
            {/* Top: Versand Summary */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="ab-total">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">Gesamtversand</div>
                  <div className="font-display text-3xl font-bold mt-1">{_num(data.total_sends)}</div>
                </div>
                <div className="flex gap-2">
                  <SplitBar label="A" value={a.sends} total={data.total_sends} color="bg-sky-400" />
                  <SplitBar label="B" value={b.sends} total={data.total_sends} color="bg-amber-400" />
                </div>
              </div>
            </div>

            {/* Variant comparison cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <VariantCard
                letter="A" label="Zahlen-fokussiert" color="sky"
                data={a}
                isWinnerSoft={data.winner_soft === "a"}
                isWinnerHard={data.winner_hard === "a"}
                testid="ab-variant-a"
              />
              <VariantCard
                letter="B" label="Story-fokussiert" color="amber"
                data={b}
                isWinnerSoft={data.winner_soft === "b"}
                isWinnerHard={data.winner_hard === "b"}
                testid="ab-variant-b"
              />
            </div>

            {/* Significance */}
            <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-3" data-testid="ab-significance">
              <h2 className="font-display text-lg font-bold inline-flex items-center gap-2">
                <BarChart3 size={16} className="text-violet-400" /> Statistische Signifikanz
              </h2>
              <SigRow label="Soft-Conversion (Status weiter)" sig={sigSoft} winner={data.winner_soft} />
              <SigRow label="Hard-Conversion (Auftrag unterschrieben)" sig={sigHard} winner={data.winner_hard} />
              <p className="text-[12px] text-zinc-300 font-medium">
                Chi²-Test, df=1, 95 %-Niveau ≥ 3,841 · 99 %-Niveau ≥ 6,635. Mindestens n≥30 + erwartete Häufigkeiten ≥ 5 erforderlich.
              </p>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function SplitBar({ label, value, total, color }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="text-right">
      <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1">{label}: {value} ({pct.toFixed(0)}%)</div>
      <div className="w-32 h-2 bg-zinc-800 rounded overflow-hidden">
        <div className={`h-2 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function VariantCard({ letter, label, color, data, isWinnerSoft, isWinnerHard, testid }) {
  const c = color === "sky" ? "text-sky-300 border-sky-500/40 bg-sky-500/5" : "text-amber-300 border-amber-500/40 bg-amber-500/5";
  return (
    <div className={`border rounded-2xl p-5 ${c}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="font-display text-3xl font-bold">{letter}</div>
          <div className="text-[13px] text-zinc-300">{label}</div>
        </div>
        {(isWinnerSoft || isWinnerHard) && (
          <span className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40" data-testid={`${testid}-winner-badge`}>
            <Trophy size={11} /> Gewinner
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Metric label="Versand" value={_num(data.sends)} testid={`${testid}-sends`} />
        <Metric label="Soft-Konv." value={_pct(data.soft_conv_pct)} accent={isWinnerSoft ? "green" : null} testid={`${testid}-soft`} />
        <Metric label="Hard-Konv." value={_pct(data.hard_conv_pct)} accent={isWinnerHard ? "green" : null} testid={`${testid}-hard`} />
      </div>
      <div className="mt-3 text-[12px] text-zinc-400 grid grid-cols-2 gap-x-3 gap-y-1">
        <div>Status weiter: <b className="text-zinc-200">{data.advanced || 0}</b></div>
        <div>Auftrag erhalten: <b className="text-zinc-200">{data.hard || 0}</b></div>
      </div>
    </div>
  );
}

function Metric({ label, value, accent, testid }) {
  const color = accent === "green" ? "text-[#00E575]" : "text-zinc-100";
  return (
    <div data-testid={testid}>
      <div className="text-[12px] uppercase tracking-widest text-zinc-200 font-semibold font-bold">{label}</div>
      <div className={`font-display text-xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function SigRow({ label, sig, winner }) {
  if (!sig) return null;
  const sig95 = sig.significant_95;
  const sig99 = sig.significant_99;
  return (
    <div className="flex items-center justify-between gap-3 text-[12.5px]">
      <span className="text-zinc-300">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-mono text-zinc-500">χ²={sig.chi2 ?? 0} · n={sig.n}</span>
        {sig99 ? (
          <span className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40">
            <TrendingUp size={11} /> sig. 99 % · Gewinner: {winner?.toUpperCase()}
          </span>
        ) : sig95 ? (
          <span className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#00E575]/10 text-[#00E575] border border-[#00E575]/30">
            <TrendingUp size={11} /> sig. 95 % · Gewinner: {winner?.toUpperCase()}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-zinc-800 text-zinc-400 border border-zinc-700">
            <Activity size={11} /> nicht signifikant {sig.reason ? `(${sig.reason})` : ""}
          </span>
        )}
      </div>
    </div>
  );
}
